import { Injectable } from "@angular/core";
import { UserConfiguration } from "@app/core/user/domain/user-configuration";
import { languageToString } from "@app/presentation/utils/language-to-string-converter";
import { TranslateService } from "@ngx-translate/core";
import { GetUserConfigurationUseCase } from "@app/core/user/domain/use-cases/get-user-configuration.use-case";
import { Language } from "@app/core/user/domain/language";
import { catchError, firstValueFrom, map, of, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  // Matches `defaultLanguage` in TranslateModule.forRoot (app.module.ts).
  private static readonly DEFAULT_LANGUAGE = "en";

  /**
   * Initialize localization.
   * @private
   */
  constructor(
    private translateService: TranslateService,
    private getUserConfigurationUseCase: GetUserConfigurationUseCase
  ) {}
  public initializeLocalization(): Promise<void> {
    return firstValueFrom(
      this.getUserConfigurationUseCase.execute().pipe(
        tap((response) => {
          if (response != null) {
            this.setServiceLanguages(response.availableLanguages as Language[]);
          }
          this.setUserConfigurationLanguage(response);
        }),
        map(() => undefined),
        catchError(() => {
          this.setBrowserLanguage();
          return of(undefined);
        })
      )
    );
  }

  private setServiceLanguages(availableLanguages: Language[]): void {
    // `availableLanguages` is optional: it is absent whenever we fall back to
    // the locally-cached configuration (e.g. the user-configuration request
    // fails while offline), because the cache does not persist the list. That
    // is an expected, handled condition — we keep the default langs — so return
    // silently instead of logging an error that would only add Sentry noise.
    if (!Array.isArray(availableLanguages)) {
      return;
    }
    const validLanguages = availableLanguages.filter(
      (language) => language !== undefined && language !== null
    );
    for (const language of validLanguages) {
      this.translateService.addLangs([languageToString(language)]);
    }
  }

  private setUserConfigurationLanguage(
    userConfiguration: UserConfiguration | null
  ): void {
    if (userConfiguration && userConfiguration.language != null) {
      const hasLanguage = this.translateService
        .getLangs()
        .includes(languageToString(userConfiguration.language));

      if (hasLanguage) {
        this.translateService.use(languageToString(userConfiguration.language));
        return;
      }
    }
    this.setBrowserLanguage();
  }

  private setBrowserLanguage(): void {
    const browserLanguage = this.translateService.getBrowserLang();
    // Only use the browser language if we actually ship a translation file for
    // it; otherwise requesting e.g. /assets/i18n/he.json 404s to index.html and
    // HttpClient throws "Http failure during parsing". Fall back to the default.
    const supportedLanguages: string[] = Object.values(Language)
      .filter((value): value is Language => typeof value === "number")
      .map((language) => languageToString(language));
    const language =
      browserLanguage && supportedLanguages.includes(browserLanguage)
        ? browserLanguage
        : LanguageService.DEFAULT_LANGUAGE;
    this.translateService.use(language);
  }
}
